import { BOOT_FILES_GLOBAL, BOOT_GLOBAL, type BootAnswers, type BootFiles } from '@xshop/shared';

// A drawn page's first answers, when the API served them as files rather than writing them into the page: fetched once
// the page shows and put where the API client reads written-in answers, before the storefront takes the page over. An
// answer that is the same on every page — the store, its products, its form — is a file the browser kept from the last one.

/** How long the answers may take; past it the page is drawn afresh from the API instead. */
const TIMEOUT_MS = 10_000;

/** The globals a page's answers travel under: the files it names, and the answers once they are read. */
type BootWindow = { [BOOT_FILES_GLOBAL]?: BootFiles; [BOOT_GLOBAL]?: BootAnswers };

// Fetches one named answer as the API wrote it.
async function readFile(href: string): Promise<unknown> {
  const res = await fetch(href, { signal: AbortSignal.timeout(TIMEOUT_MS) });
  if (!res.ok) throw new Error(`A first answer is gone (${res.status})`);
  return res.json() as Promise<unknown>;
}

// Puts the answers a page names in place; resolves at once for a page that wrote them in or names none.
export async function loadBootFiles(): Promise<void> {
  const page = window as unknown as BootWindow;
  const files = page[BOOT_FILES_GLOBAL];
  if (!files || page[BOOT_GLOBAL]) return;
  const read = await Promise.all(Object.entries(files).map(async ([path, href]) => [path, await readFile(href)] as const));
  page[BOOT_GLOBAL] = Object.fromEntries(read);
}
